import React, { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { timelineApi } from 'api/timeline';
import { DialogAction, DialogActionButtonProps, DialogBaseExtendProps } from 'components/_new/Dialog';
import { usePanel } from 'contexts/panelContext';
import { TimelineEventItem, TimelineEventItemTypeEnum } from 'types/Timeline';
import { Icon } from 'components/_new/Icon';
import { dateFormat } from 'utils/dateFormat';
import { Divider } from 'components/_new/Divider';
import { parseHtml } from 'utils/htmlParser';
import { useGlobal } from 'contexts/globalContext';
import { ApplicationRole } from 'types/Application';
import { AplicationRoleVisibilityTag, MandatoryTag, RequireConfirmationTag } from './misc/TimelineComponents';
import { TimelineEventCardActions } from './misc/TimelineEventCardActions';

type TimelineEventDetailsDialogProps = DialogBaseExtendProps & {
	data?: TimelineEventItem | null;
	onUpdate?: (data: TimelineEventItem) => void;
};

export const TimelineEventDetailsDialog = ({ data, onUpdate, ...dialogProps }: TimelineEventDetailsDialogProps) => {
	const { t } = useTranslation();

	const { toastRef } = useGlobal();
	const { currentApplicationData } = usePanel();

	const dateIsBefore = dayjs().isBefore(data?.endAt || data?.dayDate);
	const handleSendReminder = (event: MouseEvent) => {
		if (!sendReminderNotificationData) {
			sendReminderNotificationMutate();
		} else {
			event.preventDefault();
		}
	};

	const {
		data: sendReminderNotificationData,
		mutate: sendReminderNotificationMutate,
		isLoading: csendReminderNotificationLoading,
		reset,
	} = useMutation(
		() => timelineApi.sendReminderNotification(Number(currentApplicationData.id), Number(data?.eventId)),
		{
			onSuccess: () => {
				toastRef?.current?.show({ severity: 'success', detail: t('timeline.mailRemindSended') });
			},
		}
	);

	const handleJoinOnlineClick = (event: MouseEvent) => {
		if (data?.onlineMeetingUrl) {
			const anchor = document.createElement('a');
			anchor.target = '_blank';
			window.open(data.onlineMeetingUrl, '_blank');
		} else {
			event.preventDefault();
		}
	};

	useEffect(() => {
		if (!dialogProps.visible) {
			reset();
		}
		return () => {
			reset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialogProps.visible]);

	const actions = [
		{
			key: 'close',
			label: t('actions.close'),
			onClick: dialogProps.onHide,
			variant: 'primary-text',
			className: 'mr-auto',
		},
		data?.type === TimelineEventItemTypeEnum.EVENT &&
			dateIsBefore && {
				key: 'sendReminder',
				label: t('timeline.action.sendReminder'),
				onClick: handleSendReminder,
				loading: csendReminderNotificationLoading,
				variant: sendReminderNotificationData ? 'gray-success' : 'primary-outlined',
				disabeld: Boolean(sendReminderNotificationData) || undefined,
			},
		data?.onlineMeetingUrl && {
			key: 'joinOnline',
			label: t('timeline.action.joinOnline'),
			onClick: handleJoinOnlineClick,
			variant: 'primary',
		},
	].filter(Boolean) as DialogActionButtonProps[];

	const displayLimitInfo =
		data &&
		data.requireConfirmation &&
		data.participantLimit &&
		data.currentParticipants &&
		data.currentParticipants / data.participantLimit >= 0.5;
	const finalParticipantLimit = data ? Math.max(data.currentParticipants || 0, data.participantLimit || 0) : 0;

	return (
		<DialogAction
			{...dialogProps}
			title={t('timeline.item.details')}
			bodyClassName="flex flex-column justify-content-start align-items-start gap-4"
			actions={actions}
			withoutCancelAction
		>
			{data && (
				<>
					{(data.mandatory || data.requireConfirmation || data.forApplicationRole) && (
						<div className="flex flex-row gap-1 justify-content-start">
							{data.mandatory && <MandatoryTag />}
							{data.requireConfirmation && <RequireConfirmationTag />}
							{data.forApplicationRole && (
								<AplicationRoleVisibilityTag role={data.forApplicationRole as ApplicationRole} />
							)}
						</div>
					)}

					<div className="flex flex-column gap-0 text-lg strong">
						<span>{data.summary}</span>
						{/* {data.subtitle && <span className=" text-primary">{data.subtitle}</span>} */}
					</div>

					<div className="flex flex-row flex-wrap w-full gap-4 justify-content-between">
						<div className="flex flex-row gap-3 flex-nowrap bg-new-gray rounded-md px-2 py-1">
							<div className="flex flex-row gap-1 justify-content-center align-items-center">
								<Icon name="calendar" iconStyle="regular" className="text-purple" />
								<span className="text-nowrap">
									{dateFormat(data.startAt || data.dayDate, 'dayAndMonth')}
								</span>
							</div>
							{data.startAt && data.type !== TimelineEventItemTypeEnum.MILESTONE && (
								<div className="flex flex-row gap-1 justify-content-center align-items-center">
									<Icon name="clock" iconStyle="regular" className="text-purple" />
									<span className="nowrap">{`${dateFormat(data.startAt, 'hours')}`}</span>
									{data.endAt && (
										<span className="nowrap">{` - ${dateFormat(data.endAt, 'hours')}`}</span>
									)}
								</div>
							)}

							{displayLimitInfo && (
								<div className="flex flex-row gap-1 justify-content-center align-items-center">
									<Icon name="users" iconStyle="solid" className="text-purple" />
									<span className="text-nowrap">
										{data.currentParticipants}/{finalParticipantLimit}
									</span>
								</div>
							)}
						</div>

						<div className="flex flex-row gap-1">
							<TimelineEventCardActions data={data} onUpdate={onUpdate} />
						</div>
					</div>

					<Divider />
					<div>{data.intro}</div>
					{data.description && data.description.length > 0 && (
						<div className="bg-new-gray p-3 rounded-md user-content">
							{parseHtml(data.description || '', { nl2br: false })}
						</div>
					)}
				</>
			)}
		</DialogAction>
	);
};
